/**
 * Who, what, which programme and when, narrowing the audit trail on the server.
 *
 * @module components/admin/AuditTrailFilters
 */

import React from "react";

import { auditEventLabel } from "@/components/shared/audit-event-labels";
import { Combobox, FilterBar, FormField, SelectField, TextField } from "@/components/ui";

import {
  AUDIT_ACTIONS,
  auditFiltersActive,
  type AuditFilterValues,
  auditRangeBackwards,
  NO_AUDIT_FILTERS,
} from "./audit-trail-filters";

export interface AuditTrailFiltersProps {
  values: AuditFilterValues;
  onChange: (next: AuditFilterValues) => void;
  users: readonly { id: string; displayName: string }[];
  programmes: readonly { id: string; title: string }[];
  /** How many events are showing of how many match, once the trail has answered. */
  shown?: { shown: number; total: number };
}

const ACTION_OPTIONS = [
  { value: "", label: "Any event" },
  ...AUDIT_ACTIONS.map((action) => ({ value: action, label: auditEventLabel(action) })),
];

export function AuditTrailFilters({
  values,
  onChange,
  users,
  programmes,
  shown = undefined,
}: AuditTrailFiltersProps): React.ReactElement {
  const set =
    (key: keyof AuditFilterValues) =>
    (value: string): void => {
      onChange({ ...values, [key]: value });
    };
  const active = auditFiltersActive(values);
  return (
    <FilterBar
      aria-label="Filter the audit trail"
      summary="Filter the audit trail"
      variant="flush"
      active={active}
      defaultOpen={active}
      count={shown === undefined ? undefined : `${shown.shown} of ${shown.total} shown`}
      countTestId="admin-audit-count"
      onClear={() => {
        onChange(NO_AUDIT_FILTERS);
      }}
      data-testid="admin-audit-filters"
    >
      <div className="grid gap-3">
        <FormField label="Actor" htmlFor="audit-filter-actor">
          <Combobox
            id="audit-filter-actor"
            value={values.actor}
            onChange={set("actor")}
            options={[
              { value: "", label: "Anyone" },
              ...users.map((user) => ({ value: user.id, label: user.displayName })),
            ]}
            placeholder="Anyone"
            emptyMessage="No people match"
            data-testid="audit-filter-actor"
          />
        </FormField>
        <FormField label="Event" htmlFor="audit-filter-action">
          <SelectField
            id="audit-filter-action"
            value={values.action}
            onChange={set("action")}
            options={ACTION_OPTIONS}
            data-testid="audit-filter-action"
          />
        </FormField>
        <FormField
          label="Programme"
          htmlFor="audit-filter-programme"
          helpText="Events about the programme and its modules."
        >
          <SelectField
            id="audit-filter-programme"
            value={values.programmeId}
            onChange={set("programmeId")}
            options={[
              { value: "", label: "Any programme" },
              ...programmes.map((programme) => ({ value: programme.id, label: programme.title })),
            ]}
            data-testid="audit-filter-programme"
          />
        </FormField>
        <FormField label="From" htmlFor="audit-filter-from">
          <TextField
            id="audit-filter-from"
            type="date"
            value={values.from}
            onChange={set("from")}
            data-testid="audit-filter-from"
          />
        </FormField>
        <FormField
          label="To"
          htmlFor="audit-filter-to"
          error={auditRangeBackwards(values) ? "Ends before it starts" : undefined}
        >
          <TextField
            id="audit-filter-to"
            type="date"
            value={values.to}
            onChange={set("to")}
            aria-invalid={auditRangeBackwards(values)}
            data-testid="audit-filter-to"
          />
        </FormField>
      </div>
    </FilterBar>
  );
}
